// Landing page for project invite links: https://projects.cedarpost.com/join/<token>.
// Renders the UNAUTHENTICATED invite preview, then branches:
//   iOS Safari  → primary "Open in app" via the aihd:// custom scheme (universal-link
//                 interception is best-effort on TestFlight — Apple's AASA CDN can lag)
//                 + TestFlight install steps.
//   signed-in   → "Join as {role}" → redeem → store reload → the project.
//   signed-out  → inline Google sign-in (SignInView pattern), then join.
// The token is a secret: it is never logged and never rendered beyond this URL.

import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError, getInvitePreview, redeemInvite } from "../lib/apiClient";
import { useAuth } from "../lib/auth";
import { useStore } from "../lib/storeContext";
import { Spinner } from "./ui";

type DeadEnd = "notfound" | "expired" | "revoked";

function deadEndFromError(e: unknown): DeadEnd | null {
  if (!(e instanceof ApiError)) return null;
  if (e.status === 404 || e.code === "invite_not_found") return "notfound";
  if (e.code === "invite_expired") return "expired";
  if (e.code === "invite_revoked") return "revoked";
  return null;
}

const DEAD_END_COPY: Record<DeadEnd, { icon: string; title: string; subtitle: string }> = {
  notfound: {
    icon: "🔗",
    title: "This invite link isn't valid",
    subtitle: "Check that the whole link was copied, or ask for a new invite.",
  },
  expired: {
    icon: "⏳",
    title: "This invite has expired",
    subtitle: "Ask the project owner to send you a fresh invite link.",
  },
  revoked: {
    icon: "🚫",
    title: "This invite was revoked",
    subtitle: "Ask the project owner to send you a new invite link.",
  },
};

const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

export function JoinInviteView() {
  const { token = "" } = useParams();

  const preview = useQuery({
    queryKey: ["invite-preview", token],
    queryFn: () => getInvitePreview(token),
    retry: false,
    staleTime: 60_000,
  });

  let body;
  if (preview.isLoading) {
    body = <Spinner label="Checking your invite…" />;
  } else if (preview.error) {
    const deadEnd = deadEndFromError(preview.error);
    body = deadEnd ? (
      <DeadEndCard kind={deadEnd} />
    ) : (
      <div className="text-center">
        <p className="text-sm text-muted">Couldn&rsquo;t load this invite. Check your connection.</p>
        <button className="btn-warm mt-4 text-sm" onClick={() => preview.refetch()}>
          Try again
        </button>
      </div>
    );
  } else if (preview.data) {
    const status = preview.data.status;
    if (status === "expired" || status === "revoked") {
      body = <DeadEndCard kind={status} />;
    } else {
      body = <InviteCard token={token} preview={preview.data} />;
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-paper px-6 py-10">
      <div className="card w-full max-w-md p-8">
        <img src="/logo.png" alt="Cedar Post" className="mx-auto mb-5 h-16 w-16 rounded-2xl object-contain" />
        {body}
      </div>
    </div>
  );
}

function DeadEndCard({ kind }: { kind: DeadEnd }) {
  const c = DEAD_END_COPY[kind];
  return (
    <div className="text-center">
      <div className="text-4xl">{c.icon}</div>
      <h1 className="mt-3 text-xl font-bold text-ink">{c.title}</h1>
      <p className="mt-2 text-sm text-muted">{c.subtitle}</p>
    </div>
  );
}

function InviteCard({
  token,
  preview,
}: {
  token: string;
  preview: { projectName: string | null; inviterName: string | null; role: string };
}) {
  const roleLabel = preview.role === "viewer" ? "viewer" : "editor";
  return (
    <div className="text-center">
      <h1 className="text-xl font-bold text-ink">
        {preview.inviterName || "Someone"} invited you to{" "}
        <span className="text-accent">{preview.projectName || "a project"}</span>
      </h1>
      <p className="mt-2 text-sm text-muted">
        You&rsquo;ll join as {roleLabel === "editor" ? "an" : "a"}{" "}
        <span className="font-semibold capitalize text-ink">{roleLabel}</span>
        {roleLabel === "editor"
          ? " — you can add and edit rooms, photos, scans, and makeovers."
          : " — you can browse everything, read-only."}
      </p>

      {isIOS ? (
        <div className="mt-6 space-y-5">
          {/* Primary on iOS: the custom scheme opens the installed app immediately.
              (Links tapped in Messages/Mail open the app directly via universal links.) */}
          <a href={`aihd://join?token=${encodeURIComponent(token)}`} className="btn-warm block w-full">
            Open in app
          </a>
          <div className="rounded-card bg-cream px-4 py-3 text-left text-xs text-muted">
            <p className="font-semibold text-ink">Don&rsquo;t have the app yet?</p>
            <ol className="mt-1 list-decimal space-y-1 pl-4">
              <li>
                Install{" "}
                <a
                  href="https://apps.apple.com/app/testflight/id899247664"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-accent underline"
                >
                  TestFlight
                </a>{" "}
                from the App Store.
              </li>
              <li>Accept the AI Home Decorator TestFlight invite (check your email).</li>
              <li>Open the app, sign in, then tap this invite link again.</li>
            </ol>
            <p className="mt-2">
              Tip: invite links tapped in Messages or Mail open the app directly.
            </p>
          </div>
          <div className="border-t border-black/5 pt-4">
            <p className="mb-3 text-xs uppercase tracking-wide text-muted">Or continue on the web</p>
            <WebJoin token={token} roleLabel={roleLabel} />
          </div>
        </div>
      ) : (
        <div className="mt-6">
          <WebJoin token={token} roleLabel={roleLabel} />
        </div>
      )}
    </div>
  );
}

/** Signed-in: Join button → redeem → reload → project. Signed-out: inline Google sign-in. */
function WebJoin({ token, roleLabel }: { token: string; roleLabel: string }) {
  const navigate = useNavigate();
  const { status, googleConfigured, renderGoogleButton, signInWithGoogle } = useAuth();
  const { store, reload } = useStore();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [deadEnd, setDeadEnd] = useState<DeadEnd | null>(null);
  const btnRef = useRef<HTMLDivElement>(null);

  // GIS script may load after mount; retry briefly until the button renders
  // (same pattern as SignInView).
  const signedOut = status === "signedOut";
  useEffect(() => {
    if (!signedOut || !googleConfigured) return;
    let tries = 0;
    const id = setInterval(() => {
      tries += 1;
      if (btnRef.current) {
        btnRef.current.innerHTML = "";
        renderGoogleButton(btnRef.current);
      }
      if (tries > 20) clearInterval(id);
    }, 250);
    return () => clearInterval(id);
  }, [signedOut, googleConfigured, renderGoogleButton]);

  if (deadEnd) return <DeadEndCard kind={deadEnd} />;

  if (status !== "signedIn") {
    return (
      <div className="flex flex-col items-center gap-3">
        <p className="text-sm text-muted">Sign in to join this project.</p>
        {googleConfigured ? (
          <>
            <div ref={btnRef} />
            <button className="btn-ghost text-sm" onClick={signInWithGoogle}>
              Sign in with Google
            </button>
          </>
        ) : (
          <p className="rounded-card bg-cream px-4 py-3 text-sm text-muted">
            Google sign-in isn&rsquo;t configured yet — open this link in the app instead.
          </p>
        )}
      </div>
    );
  }

  async function join() {
    setBusy(true);
    setErr(null);
    try {
      const res = await redeemInvite(token);
      // A fresh member needs the shared project pulled; an existing member
      // already has it (alreadyMember ⇒ navigate directly) — unless this
      // browser's store predates the membership (joined on iOS meanwhile).
      // replace:true keeps the token out of the back stack (token hygiene).
      if (!res.alreadyMember || !store.get("PROJECT", res.projectId)) reload();
      navigate(`/projects/${res.projectId}`, { replace: true });
    } catch (e) {
      const dead = deadEndFromError(e);
      if (dead) {
        setDeadEnd(dead);
      } else if (e instanceof ApiError && e.code === "account_required") {
        setErr("This account can't join shared projects — sign in with Google or Apple first.");
      } else if (e instanceof ApiError && e.status === 404) {
        setErr("Sharing isn't available yet.");
      } else {
        setErr(e instanceof Error ? e.message : "Couldn't join the project.");
      }
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <button className="btn-warm w-full disabled:opacity-50" onClick={join} disabled={busy}>
        {busy ? "Joining…" : `Join as ${roleLabel}`}
      </button>
      {err && <p className="text-sm text-red-500">{err}</p>}
    </div>
  );
}
